import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { SponsorPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PinRowButton } from "@/components/pin-row-button";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

export default function SponsorsPage() {
  const [selected, setSelected] = useState<SponsorPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds, activeSponsorIds } = usePinFilters();
  const hasPinFilter = activeOrgIds.length > 0 || activeSponsorIds.length > 0;
  const pinKey = `${activeOrgIds.join(",")}|${activeSponsorIds.join(",")}`;

  async function handleDelete(sponsorId: string) {
    if (!confirm("Delete this sponsor? This action cannot be undone.")) return;
    try {
      await api.deleteSponsor(sponsorId);
      if (selected?.id === sponsorId) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete sponsor");
    }
  }

  // Pin filtering is page-local; when active, fetch the full set and apply
  // search/sort/pagination + the pin filter client-side. Otherwise server-side.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: SponsorPublic[]; total: number }> => {
      if (hasPinFilter) {
        const res = await api.getSponsors();
        const scoped = res.sponsors.filter(
          (s) => activeSponsorIds.includes(s.id) || activeOrgIds.includes(s.organization_id),
        );
        return applyClientListParams(scoped, params, {
          searchFields: (s) => [s.name, s.industry, s.contact_name, s.contact_email],
          sortAccessors: { name: (s) => s.name },
          defaultSort: "name",
        });
      }
      const res = await api.getSponsors({
        page: params.page, per_page: params.per_page,
        search: params.search, sort: params.sort, order: params.order,
      });
      return { data: res.sponsors, total: res.total ?? res.sponsors.length };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [pinKey],
  );

  const columns: DataTableColumn<SponsorPublic>[] = [
    {
      key: "name", header: "Name", sortable: true, className: "font-medium",
      render: (s) => <Link to={`/sponsors/${s.slug}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{s.name}</Link>,
    },
    { key: "industry", header: "Industry", className: "text-muted-foreground", render: (s) => s.industry ?? "--" },
    { key: "contact", header: "Contact", className: "text-muted-foreground", render: (s) => s.contact_name },
    { key: "email", header: "Email", className: "text-muted-foreground", render: (s) => s.contact_email },
    {
      key: "actions", header: "", headerClassName: "w-16",
      render: (sponsor) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <PinRowButton pinType="sponsor" entityType="Sponsor" entityId={sponsor.id} />
          {sponsor._actions.includes("edit") && (
            <Link to={`/sponsors/${sponsor.slug}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="edit">
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {sponsor._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(sponsor.id)} data-action="delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="sponsors-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Sponsors</h1>
            <p className="text-muted-foreground">Manage sponsors in your organization</p>
          </div>
          <div className="flex gap-2">
            <Link to="/sponsors/new" className={buttonVariants()} data-action="create">
              <Plus className="mr-2 h-4 w-4" />
              Add Sponsor
            </Link>
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        <PinFilterBar entityType="Organization" />
        <PinFilterBar entityType="Sponsor" />

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${pinKey}:${refreshTick}`}
            rowKey={(s) => s.id}
            rowProps={(s) => ({ "data-item": "sponsor", "data-item-id": s.id })}
            rowClassName={(s) => (selected?.id === s.id ? "bg-muted" : undefined)}
            searchPlaceholder="Search sponsors..."
            defaultSort="name"
            emptyMessage="No sponsors yet."
            onRowClick={(s) => setSelected(s)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Sponsor">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Industry">{selected.industry ?? "--"}</PreviewField>
            <PreviewField label="Contact">{selected.contact_name}</PreviewField>
            <PreviewField label="Email">{selected.contact_email}</PreviewField>
            {selected.website_url && <PreviewField label="Website">{selected.website_url}</PreviewField>}
          </>
        )}
      </PreviewPane>
    </div>
  );
}
